import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { SUBSCRIPTION_CONFIG } from '../../config/subscription';
import './UpgradeModal.css';

/**
 * Upgrade Modal Component
 *
 * Shown when user tries to access premium features without subscription
 * Displays pricing and trial information
 *
 * Usage:
 *   <UpgradeModal isOpen={true} onClose={() => setOpen(false)} />
 */
export function UpgradeModal({ isOpen, onClose, feature }) {
  const { startCheckout, isTrialing, daysRemaining, checkoutLoading } = useSubscription();

  if (!isOpen) return null;

  const handleStartTrial = async () => {
    try {
      await startCheckout('personal');
    } catch (err) {
      console.error('Error starting trial:', err);
      alert('Er ging iets mis bij het starten van de trial. Probeer het opnieuw.');
    }
  };

  const handleContactEnterprise = () => {
    window.location.href = 'mailto:pim.südmeier@humanaize.nl?subject=Enterprise Plan - Job Recruitment Platform';
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal__close" onClick={onClose}>
          ✕
        </button>

        <div className="upgrade-modal__header">
          <h2 className="upgrade-modal__title">
            🚀 Upgrade naar Premium
          </h2>
          <p className="upgrade-modal__subtitle">
            {feature ? `"${feature}" is een premium functie` : 'Ontgrendel alle functies'}
          </p>
        </div>

        {/* Personal Plan */}
        <div className="upgrade-modal__plan upgrade-modal__plan--featured">
          <div className="upgrade-modal__plan-badge">Meest gekozen</div>
          <h3 className="upgrade-modal__plan-name">Personal Plan</h3>
          <div className="upgrade-modal__price">
            <span className="upgrade-modal__price-amount">€{SUBSCRIPTION_CONFIG.PERSONAL_PLAN_PRICE}</span>
            <span className="upgrade-modal__price-period">/maand</span>
          </div>

          {!isTrialing && (
            <div className="upgrade-modal__trial">
              ✨ {SUBSCRIPTION_CONFIG.TRIAL_PERIOD_DAYS} dagen gratis proberen
            </div>
          )}

          <ul className="upgrade-modal__features">
            <li>✅ Onbeperkt vacatures genereren</li>
            <li>✅ AI-powered vacancy improvement</li>
            <li>✅ Live chat met Job</li>
            <li>✅ GPT-4 powered summarization</li>
            <li>✅ Voice interaction</li>
            <li>✅ Alle toekomstige features</li>
          </ul>

          <button
            className="upgrade-modal__cta upgrade-modal__cta--primary"
            onClick={handleStartTrial}
            disabled={checkoutLoading}
          >
            {checkoutLoading
              ? '⏳ Checkout wordt geladen...'
              : isTrialing
              ? `Activeer (${daysRemaining} dagen trial over)`
              : `Start ${SUBSCRIPTION_CONFIG.TRIAL_PERIOD_DAYS}-Dagen Gratis Trial`}
          </button>

          <p className="upgrade-modal__note">
            {SUBSCRIPTION_CONFIG.TRIAL_PERIOD_DAYS} dagen gratis, daarna €{SUBSCRIPTION_CONFIG.PERSONAL_PLAN_PRICE}/maand · Annuleer elk moment
          </p>
        </div>

        {/* Enterprise Plan */}
        <div className="upgrade-modal__plan">
          <h3 className="upgrade-modal__plan-name">Enterprise Plan</h3>
          <div className="upgrade-modal__price">
            <span className="upgrade-modal__price-amount">Op maat</span>
          </div>

          <ul className="upgrade-modal__features">
            <li>✅ Alles van Personal Plan</li>
            <li>✅ Aangepaste facturering</li>
            <li>✅ Dedicated support</li>
            <li>✅ Custom branding</li>
            <li>✅ API toegang</li>
            <li>✅ Team collaboration</li>
            <li>✅ Volume kortingen</li>
          </ul>

          <button
            className="upgrade-modal__cta upgrade-modal__cta--secondary"
            onClick={handleContactEnterprise}
          >
            Neem contact op
          </button>

          <p className="upgrade-modal__note">
            📧 pim.südmeier@humanaize.nl
          </p>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;
